import { DatasetItem } from '../../../types/types';

/**
 * Whether a definition item is worth offering for printing.
 *
 * A report may legitimately want a column the grid hides — plu, sku, ean, note
 * and unit cost are all `visible: false` in the shipped definitions yet are real
 * data someone prints. So "hidden in the grid" is not the filter; "technical" is.
 *
 * Excluded are the columns that carry no meaning on paper: identifiers and
 * foreign keys, sync and audit timestamps, and ordering counters. Everything
 * else is offered, with hidden ones marked so the palette can separate them.
 */

export type DatasetItemVisibility = 'grid' | 'hidden' | 'technical';

const TECHNICAL_NAMES = [
  'id',
  'changexid',
  'change_xid',
  'createdat',
  'updatedat',
  'created_at',
  'updated_at',
  'sortorder',
  'sort_order',
  'rowversion',
  'version',
];

/**
 * A trailing "Id" marks a foreign key (productId, categoryId, taxModelId…).
 * Matched on the suffix rather than a list, so a dataset added later is covered
 * without anyone remembering to extend this.
 */
const FOREIGN_KEY_SUFFIX = /Id$/u;
const INTERNAL_CODE_SUFFIX = /(?:Code|_code)$/u;
const I18N_SUFFIX = /(?:I18n|_i18n)$/u;
const STRUCTURED_TYPES = new Set(['json', 'jsonb', 'object']);

export const isTechnicalItem = (item: DatasetItem): boolean => {
  const name = item.name ?? '';
  const normalizedName = name.toLowerCase();
  if (
    TECHNICAL_NAMES.includes(normalizedName) ||
    normalizedName === 'code' ||
    normalizedName.startsWith('sync') ||
    normalizedName.endsWith('syncedat') ||
    INTERNAL_CODE_SUFFIX.test(name) ||
    I18N_SUFFIX.test(name) ||
    STRUCTURED_TYPES.has(item.type?.toLowerCase() ?? '')
  ) {
    return true;
  }

  return FOREIGN_KEY_SUFFIX.test(name);
};

export const classifyDatasetItem = (
  item: DatasetItem
): DatasetItemVisibility => {
  if (isTechnicalItem(item)) {
    return 'technical';
  }
  // A string `visible` is a JSONata expression needing a row, so it counts as
  // shown — the same rule the renderer and the export apply.
  return item.visible === false ? 'hidden' : 'grid';
};

/** Items a report can use, grid columns first, hidden ones after. */
export const printableDatasetItems = (
  items: DatasetItem[]
): {
  item: DatasetItem;
  visibility: Exclude<DatasetItemVisibility, 'technical'>;
}[] => {
  const classified = items
    .filter((item) => Boolean(item.name))
    .map((item) => ({ item, visibility: classifyDatasetItem(item) }))
    .filter(
      (
        entry
      ): entry is {
        item: DatasetItem;
        visibility: Exclude<DatasetItemVisibility, 'technical'>;
      } => entry.visibility !== 'technical'
    );

  return [
    ...classified.filter((entry) => entry.visibility === 'grid'),
    ...classified.filter((entry) => entry.visibility === 'hidden'),
  ];
};
